import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Server, Shield, TrendingUp, DollarSign, Play, Eye, Mail, Users, Calendar, ShoppingBag, BarChart3, Radio, Music, ExternalLink, Plus, Send, Image, Clock, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
interface CreatorDashboardProps {
  onBack?: () => void;
}
const trafficData = [{
  name: 'Mon',
  views: 2400,
  streams: 1800
}, {
  name: 'Tue',
  views: 1398,
  streams: 2200
}, {
  name: 'Wed',
  views: 5800,
  streams: 3200
}, {
  name: 'Thu',
  views: 3908,
  streams: 2800
}, {
  name: 'Fri',
  views: 4800,
  streams: 4100
}, {
  name: 'Sat',
  views: 7800,
  streams: 5200
}, {
  name: 'Sun',
  views: 6200,
  streams: 4800
}];
const revenueData = [{
  name: 'Jan',
  streaming: 1200,
  store: 800,
  ads: 400
}, {
  name: 'Feb',
  streaming: 1800,
  store: 1200,
  ads: 600
}, {
  name: 'Mar',
  streaming: 2400,
  store: 900,
  ads: 800
}, {
  name: 'Apr',
  streaming: 3200,
  store: 1500,
  ads: 1000
}, {
  name: 'May',
  streaming: 2800,
  store: 2100,
  ads: 1200
}, {
  name: 'Jun',
  streaming: 4100,
  store: 2800,
  ads: 1400
}];
const emailStats = [{
  name: 'Sent',
  value: 12400
}, {
  name: 'Opened',
  value: 8200
}, {
  name: 'Clicked',
  value: 3100
}];
const scheduledPosts = [{
  platform: 'Instagram',
  content: 'New single announcement 🎵',
  time: 'Today, 6:00 PM',
  status: 'scheduled'
}, {
  platform: 'Twitter/X',
  content: 'Behind the scenes studio session',
  time: 'Tomorrow, 12:00 PM',
  status: 'scheduled'
}, {
  platform: 'TikTok',
  content: 'Dance challenge clip',
  time: 'Wed, 3:00 PM',
  status: 'draft'
}, {
  platform: 'YouTube',
  content: 'Full music video premiere',
  time: 'Fri, 8:00 PM',
  status: 'scheduled'
}];
const storeProducts = [{
  name: 'Limited Edition Hoodie',
  price: 65,
  sold: 142,
  stock: 58
}, {
  name: 'Signed Vinyl LP',
  price: 45,
  sold: 89,
  stock: 11
}, {
  name: 'VIP Meet & Greet Pass',
  price: 150,
  sold: 24,
  stock: 6
}];
const COLORS = ['#FFD700', '#FF0000', '#4F46E5'];
export function CreatorDashboard({
  onBack
}: CreatorDashboardProps) {
  const [activeSection, setActiveSection] = useState('overview');
  const sections = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'hosting',
    label: 'Hosting & Domains'
  }, {
    id: 'streaming',
    label: 'Streaming'
  }, {
    id: 'content',
    label: 'Content & Traffic'
  }, {
    id: 'email',
    label: 'Email & CRM'
  }, {
    id: 'social',
    label: 'Social Scheduler'
  }, {
    id: 'store',
    label: 'E-Commerce'
  }, {
    id: 'revenue',
    label: 'Revenue'
  }];
  return <div className="min-h-screen bg-[#0D0D0D] text-white font-['Montserrat']" data-id="element-296">
      {/* Top Bar */}
      <header className="bg-[#0D0D0D]/90 backdrop-blur-xl border-b border-white/5 px-6 py-3 sticky top-0 z-50" data-id="element-297">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between" data-id="element-298">
          <div className="flex items-center gap-4" data-id="element-299">
            {onBack && <button onClick={onBack} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-300">
                <ArrowLeft size={20} className="text-gray-400" data-id="element-301" />
              </button>}
            <div className="flex items-center gap-2" data-id="element-302">
              <div className="w-8 h-8 bg-[#FFD700] rounded flex items-center justify-center" data-id="element-303">
                <span className="text-black font-['Archivo_Black'] text-lg" data-id="element-304">
                  H
                </span>
              </div>
              <span className="font-['Archivo_Black'] text-lg tracking-tighter uppercase" data-id="element-305">
                Creator Dashboard
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-306">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-id="element-307">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-308"></div>
              <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest" data-id="element-309">
                All Systems Online
              </span>
            </div>
            <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest hover:bg-white transition-all" data-id="element-310">
              Upgrade Plan
            </button>
          </div>
        </div>
      </header>

      {/* Section Tabs */}
      <div className="border-b border-white/5 px-6 bg-[#0D0D0D]/50 backdrop-blur-sm sticky top-[57px] z-40" data-id="element-311">
        <div className="max-w-[1800px] mx-auto flex gap-1 overflow-x-auto py-2" data-id="element-312">
          {sections.map(s => <button key={s.id} onClick={() => setActiveSection(s.id)} className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-[#FFD700] text-black' : 'text-gray-500 hover:text-white hover:bg-white/5'}`} data-id="element-313">
              {s.label}
            </button>)}
        </div>
      </div>

      <main className="max-w-[1800px] mx-auto p-6 space-y-6" data-id="element-314">
        {/* Overview Section */}
        {activeSection === 'overview' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-315">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-316">
              {[{
            label: 'Monthly Visitors',
            value: '48.2K',
            change: '+12.4%',
            icon: Eye,
            color: 'text-[#FFD700]'
          }, {
            label: 'Stream Revenue',
            value: '$4,120',
            change: '+24.1%',
            icon: Play,
            color: 'text-emerald-500'
          }, {
            label: 'Store Sales',
            value: '$2,840',
            change: '+8.7%',
            icon: ShoppingBag,
            color: 'text-blue-500'
          }, {
            label: 'Email Subscribers',
            value: '12.4K',
            change: '+340',
            icon: Mail,
            color: 'text-purple-500'
          }].map((stat, i) => <div key={i} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-317">
                  <div className="flex items-center justify-between mb-4" data-id="element-318">
                    <stat.icon size={20} className={stat.color} data-id="element-319" />
                    <span className="text-[10px] font-bold text-emerald-500 uppercase" data-id="element-320">
                      {stat.change}
                    </span>
                  </div>
                  <p className="text-2xl font-['Archivo_Black'] tracking-tight" data-id="element-321">
                    {stat.value}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-322">
                    {stat.label}
                  </p>
                </div>)}
            </div>

            {/* Traffic + Revenue Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-323">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-324">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-325">
                  Content Traffic (7 Days)
                </h3>
                <div className="h-[250px]" data-id="element-326">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-327">
                    <AreaChart data={trafficData} data-id="element-328">
                      <defs data-id="element-329">
                        <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-330">
                          <stop offset="5%" stopColor="#FFD700" stopOpacity={0.2} data-id="element-331" />
                          <stop offset="95%" stopColor="#FFD700" stopOpacity={0} data-id="element-332" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-333" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-334" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-335" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} data-id="element-336" />
                      <Area type="monotone" dataKey="views" stroke="#FFD700" strokeWidth={2} fill="url(#goldGrad)" data-id="element-337" />
                      <Area type="monotone" dataKey="streams" stroke="#FF0000" strokeWidth={2} fill="transparent" strokeDasharray="5 5" data-id="element-338" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-339">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-340">
                  Revenue Breakdown (6 Months)
                </h3>
                <div className="h-[250px]" data-id="element-341">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-342">
                    <BarChart data={revenueData} data-id="element-343">
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-344" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-345" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-346" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} data-id="element-347" />
                      <Bar dataKey="streaming" fill="#FFD700" radius={[4, 4, 0, 0]} data-id="element-348" />
                      <Bar dataKey="store" fill="#FF0000" radius={[4, 4, 0, 0]} data-id="element-349" />
                      <Bar dataKey="ads" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-350" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-6 mt-4" data-id="element-351">
                  {[{
                label: 'Streaming',
                color: 'bg-[#FFD700]'
              }, {
                label: 'Store',
                color: 'bg-[#FF0000]'
              }, {
                label: 'Ads',
                color: 'bg-[#4F46E5]'
              }].map((l, i) => <div key={i} className="flex items-center gap-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-352">
                      <div className={`w-2 h-2 rounded-full ${l.color}`} data-id="element-353"></div>
                      {l.label}
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Hosting & Domains */}
        {activeSection === 'hosting' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-354">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-355">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-356">
                <div className="flex items-center gap-3 mb-6" data-id="element-357">
                  <Server size={20} className="text-[#FFD700]" data-id="element-358" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-359">
                    Hosting Status
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-360">
                  {[{
                label: 'Plan',
                value: 'Creator Pro ($149/mo)'
              }, {
                label: 'Storage',
                value: '45 GB / 100 GB'
              }, {
                label: 'Bandwidth',
                value: '820 GB / Unlimited'
              }, {
                label: 'Uptime',
                value: '99.99%'
              }].map((item, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-361">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-362">
                        {item.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-363">{item.value}</span>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-364">
                <div className="flex items-center gap-3 mb-6" data-id="element-365">
                  <Globe size={20} className="text-[#FFD700]" data-id="element-366" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-367">
                    Domains
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-368">
                  {[{
                domain: 'artistname.com',
                status: 'Active',
                ssl: true
              }, {
                domain: 'artistname.store',
                status: 'Active',
                ssl: true
              }, {
                domain: 'artistname.live',
                status: 'Pending',
                ssl: false
              }].map((d, i) => <div key={i} className="flex items-center justify-between p-3 bg-black/40 rounded-lg border border-white/5" data-id="element-369">
                      <div data-id="element-370">
                        <p className="text-sm font-bold" data-id="element-371">{d.domain}</p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-372">
                          {d.ssl && <span className="text-[8px] font-bold text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded uppercase" data-id="element-373">
                              SSL+
                            </span>}
                          <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${d.status === 'Active' ? 'text-emerald-500 bg-emerald-500/10' : 'text-amber-500 bg-amber-500/10'}`} data-id="element-374">
                            {d.status}
                          </span>
                        </div>
                      </div>
                      <ExternalLink size={14} className="text-gray-600" data-id="element-375" />
                    </div>)}
                </div>
                <button className="mt-4 w-full py-2.5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-id="element-376">
                  <Plus size={14} data-id="element-377" /> Add Domain
                </button>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-378">
                <div className="flex items-center gap-3 mb-6" data-id="element-379">
                  <Shield size={20} className="text-emerald-500" data-id="element-380" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-381">
                    SSL & Security
                  </h3>
                </div>
                <div className="text-center py-8" data-id="element-382">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4" data-id="element-383">
                    <Shield size={32} className="text-emerald-500" data-id="element-384" />
                  </div>
                  <p className="text-2xl font-['Archivo_Black']" data-id="element-385">SSL+ Active</p>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-2" data-id="element-386">
                    Wildcard Certificate
                  </p>
                  <p className="text-[10px] text-gray-600 mt-1" data-id="element-387">
                    Expires: Dec 2025
                  </p>
                </div>
                <div className="space-y-2 mt-4" data-id="element-388">
                  {['DDoS Protection', 'Firewall Active', 'Daily Backups'].map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-gray-400" data-id="element-389">
                        <CheckCircle2 size={14} className="text-emerald-500" data-id="element-390" />
                        {f}
                      </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Streaming */}
        {activeSection === 'streaming' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-391">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-392">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-393">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-394">
                  Monetization Tracking
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-395">
                  {[{
                label: 'Total Streams',
                value: '284K',
                sub: 'This month'
              }, {
                label: 'Revenue',
                value: '$4,120',
                sub: '+24% vs last month'
              }, {
                label: 'Avg. Per Stream',
                value: '$0.0145',
                sub: 'Above industry avg'
              }].map((s, i) => <div key={i} className="bg-black/40 rounded-lg p-4 border border-white/5" data-id="element-396">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-397">
                        {s.label}
                      </p>
                      <p className="text-xl font-['Archivo_Black'] mt-1" data-id="element-398">
                        {s.value}
                      </p>
                      <p className="text-[10px] text-gray-600 mt-1" data-id="element-399">{s.sub}</p>
                    </div>)}
                </div>
                <div className="h-[200px]" data-id="element-400">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-401">
                    <LineChart data={trafficData} data-id="element-402">
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-403" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-404" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-405" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }} data-id="element-406" />
                      <Line type="monotone" dataKey="streams" stroke="#FFD700" strokeWidth={3} dot={false} data-id="element-407" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-408">
                <div className="flex items-center gap-3 mb-6" data-id="element-409">
                  <Radio size={20} className="text-[#FF0000]" data-id="element-410" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-411">
                    Live Stream
                  </h3>
                </div>
                <div className="text-center py-8" data-id="element-412">
                  <div className="w-16 h-16 rounded-full bg-[#FF0000]/20 border border-[#FF0000]/30 flex items-center justify-center mx-auto mb-4" data-id="element-413">
                    <Play size={24} className="text-[#FF0000] ml-0.5" data-id="element-414" />
                  </div>
                  <p className="font-bold" data-id="element-415">Ready to Go Live</p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-416">
                    Wowza Integration Active
                  </p>
                </div>
                <button className="w-full py-3 bg-[#FF0000] text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-colors" data-id="element-417">
                  Start Live Stream
                </button>
              </div>
            </div>
          </motion.div>}

        {/* Content & Traffic */}
        {activeSection === 'content' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-418">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-419">
              <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-420">
                Content Performance & Traffic Sources
              </h3>
              <div className="h-[300px]" data-id="element-421">
                <ResponsiveContainer width="100%" height="100%" data-id="element-422">
                  <AreaChart data={trafficData} data-id="element-423">
                    <defs data-id="element-424">
                      <linearGradient id="goldGrad2" x1="0" y1="0" x2="0" y2="1" data-id="element-425">
                        <stop offset="5%" stopColor="#FFD700" stopOpacity={0.3} data-id="element-426" />
                        <stop offset="95%" stopColor="#FFD700" stopOpacity={0} data-id="element-427" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-428" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-429" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-430" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }} data-id="element-431" />
                    <Area type="monotone" dataKey="views" stroke="#FFD700" strokeWidth={2} fill="url(#goldGrad2)" name="Page Views" data-id="element-432" />
                    <Area type="monotone" dataKey="streams" stroke="#FF0000" strokeWidth={2} fill="transparent" strokeDasharray="5 5" name="Stream Views" data-id="element-433" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-434">
              {[{
            source: 'Direct',
            visits: '18.2K',
            pct: '38%'
          }, {
            source: 'Social Media',
            visits: '12.4K',
            pct: '26%'
          }, {
            source: 'Search (SEO)',
            visits: '10.1K',
            pct: '21%'
          }, {
            source: 'Referral',
            visits: '7.5K',
            pct: '15%'
          }].map((s, i) => <div key={i} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-id="element-435">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-436">
                    {s.source}
                  </p>
                  <p className="text-xl font-['Archivo_Black'] mt-1" data-id="element-437">
                    {s.visits}
                  </p>
                  <p className="text-xs text-[#FFD700] font-bold mt-1" data-id="element-438">
                    {s.pct} of total
                  </p>
                </div>)}
            </div>
          </motion.div>}

        {/* Email & CRM */}
        {activeSection === 'email' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-439">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-440">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-441">
                <div className="flex items-center justify-between mb-6" data-id="element-442">
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-443">
                    Email Campaigns
                  </h3>
                  <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest" data-id="element-444">
                    <Plus size={14} className="inline mr-1" data-id="element-445" /> New Campaign
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-446">
                  {emailStats.map((s, i) => <div key={i} className="bg-black/40 rounded-lg p-4 border border-white/5 text-center" data-id="element-447">
                      <p className="text-2xl font-['Archivo_Black']" data-id="element-448">
                        {(s.value / 1000).toFixed(1)}K
                      </p>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-449">
                        {s.name}
                      </p>
                    </div>)}
                </div>
                <div className="space-y-3" data-id="element-450">
                  {[{
                subject: 'New Single Drop — Listen Now 🎵',
                sent: '12.4K',
                opened: '67%',
                date: 'Oct 12'
              }, {
                subject: 'Tour Dates Announced!',
                sent: '11.8K',
                opened: '72%',
                date: 'Oct 5'
              }, {
                subject: 'Exclusive Merch Pre-Sale',
                sent: '10.2K',
                opened: '58%',
                date: 'Sep 28'
              }].map((c, i) => <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-white/5" data-id="element-451">
                      <div data-id="element-452">
                        <p className="text-sm font-bold" data-id="element-453">{c.subject}</p>
                        <p className="text-[10px] text-gray-500 font-bold mt-1" data-id="element-454">
                          {c.date} • Sent to {c.sent}
                        </p>
                      </div>
                      <span className="text-xs font-bold text-[#FFD700]" data-id="element-455">
                        {c.opened} open rate
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-456">
                <div className="flex items-center gap-3 mb-6" data-id="element-457">
                  <Users size={20} className="text-purple-500" data-id="element-458" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-459">CRM</h3>
                </div>
                <div className="space-y-4" data-id="element-460">
                  {[{
                label: 'Total Contacts',
                value: '12,400'
              }, {
                label: 'VIP Fans',
                value: '842'
              }, {
                label: 'New This Month',
                value: '+340'
              }, {
                label: 'Engagement Rate',
                value: '67%'
              }].map((s, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-461">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-462">
                        {s.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-463">{s.value}</span>
                    </div>)}
                </div>
                <button className="mt-6 w-full py-2.5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors" data-id="element-464">
                  Manage Contacts
                </button>
              </div>
            </div>
          </motion.div>}

        {/* Social Scheduler */}
        {activeSection === 'social' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-465">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-466">
              <div className="flex items-center justify-between mb-6" data-id="element-467">
                <h3 className="font-bold uppercase tracking-wider" data-id="element-468">
                  Social Media Post Scheduler
                </h3>
                <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-469">
                  <Plus size={14} data-id="element-470" /> Schedule Post
                </button>
              </div>
              <div className="space-y-3" data-id="element-471">
                {scheduledPosts.map((post, i) => <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-white/5 hover:border-[#FFD700]/20 transition-colors" data-id="element-472">
                    <div className="flex items-center gap-4" data-id="element-473">
                      <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center" data-id="element-474">
                        <span className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-475">
                          {post.platform.slice(0, 2)}
                        </span>
                      </div>
                      <div data-id="element-476">
                        <p className="text-sm font-bold" data-id="element-477">{post.content}</p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-478">
                          <span className="text-[10px] font-bold text-gray-500" data-id="element-479">
                            {post.platform}
                          </span>
                          <span className="text-[10px] text-gray-600" data-id="element-480">•</span>
                          <Clock size={10} className="text-gray-600" data-id="element-481" />
                          <span className="text-[10px] text-gray-500" data-id="element-482">
                            {post.time}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${post.status === 'scheduled' ? 'bg-[#FFD700]/10 text-[#FFD700]' : 'bg-gray-800 text-gray-500'}`} data-id="element-483">
                      {post.status}
                    </span>
                  </div>)}
              </div>
            </div>
          </motion.div>}

        {/* E-Commerce */}
        {activeSection === 'store' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-484">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-485">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-486">
                <div className="flex items-center justify-between mb-6" data-id="element-487">
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-488">
                    Store Products
                  </h3>
                  <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-489">
                    <Plus size={14} data-id="element-490" /> Add Product
                  </button>
                </div>
                <table className="w-full" data-id="element-491">
                  <thead data-id="element-492">
                    <tr className="text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-white/5" data-id="element-493">
                      <th className="text-left py-3" data-id="element-494">Product</th>
                      <th className="text-left py-3" data-id="element-495">Price</th>
                      <th className="text-left py-3" data-id="element-496">Sold</th>
                      <th className="text-left py-3" data-id="element-497">Stock</th>
                      <th className="text-right py-3" data-id="element-498">Revenue</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-499">
                    {storeProducts.map((p, i) => <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors" data-id="element-500">
                        <td className="py-4 text-sm font-bold" data-id="element-501">{p.name}</td>
                        <td className="py-4 text-sm text-gray-400" data-id="element-502">
                          ${p.price}
                        </td>
                        <td className="py-4 text-sm text-gray-400" data-id="element-503">{p.sold}</td>
                        <td className="py-4" data-id="element-504">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.stock < 15 ? 'bg-[#FF0000]/10 text-[#FF0000]' : 'bg-emerald-500/10 text-emerald-500'}`} data-id="element-505">
                            {p.stock} left
                          </span>
                        </td>
                        <td className="py-4 text-sm font-bold text-right text-[#FFD700]" data-id="element-506">
                          ${(p.price * p.sold).toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-507">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-508">
                  Store Stats
                </h3>
                <div className="space-y-4" data-id="element-509">
                  {[{
                label: 'Total Revenue',
                value: '$18,240'
              }, {
                label: 'Orders This Month',
                value: '255'
              }, {
                label: 'Avg Order Value',
                value: '$71.50'
              }, {
                label: 'Conversion Rate',
                value: '3.2%'
              }].map((s, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-510">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-511">
                        {s.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-512">{s.value}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Revenue */}
        {activeSection === 'revenue' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-513">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-514">
              {[{
            label: 'Streaming',
            value: '$4,120',
            icon: Music
          }, {
            label: 'Storefront',
            value: '$2,840',
            icon: ShoppingBag
          }, {
            label: 'Ad Revenue',
            value: '$1,200',
            icon: BarChart3
          }, {
            label: 'Total Revenue',
            value: '$8,160',
            icon: DollarSign
          }].map((s, i) => <div key={i} className={`border border-white/5 rounded-xl p-6 ${i === 3 ? 'bg-[#FFD700]/10 border-[#FFD700]/20' : 'bg-[#1A1A1A]'}`} data-id="element-515">
                  <s.icon size={20} className={i === 3 ? 'text-[#FFD700]' : 'text-gray-500'} data-id="element-516" />
                  <p className="text-2xl font-['Archivo_Black'] mt-3" data-id="element-517">
                    {s.value}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-518">
                    {s.label}
                  </p>
                </div>)}
            </div>

            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-519">
              <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-520">
                Revenue Over Time
              </h3>
              <div className="h-[300px]" data-id="element-521">
                <ResponsiveContainer width="100%" height="100%" data-id="element-522">
                  <BarChart data={revenueData} data-id="element-523">
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-524" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-525" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-526" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }} data-id="element-527" />
                    <Bar dataKey="streaming" stackId="a" fill="#FFD700" radius={[0, 0, 0, 0]} data-id="element-528" />
                    <Bar dataKey="store" stackId="a" fill="#FF0000" data-id="element-529" />
                    <Bar dataKey="ads" stackId="a" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-530" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>}
      </main>
    </div>;
}